import { useId, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Bot, Check, ChevronDown, ChevronUp, Pencil, Trash2, UserRound, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import { cn } from '@/lib/utils'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import { normalizeTaskCommentBody } from '@/frontend/features/Fde/taskCommentFormatting'
import type { FdeCopy, ProjectTaskComment } from '@/frontend/features/Fde/types'

interface Props {
  comment: ProjectTaskComment
  projectId: string
  taskId: string
  copy: FdeCopy
}

export default function ProjectTaskCommentItem({ comment, projectId, taskId, copy }: Props) {
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const bodyId = useId()
  const normalizedBody = normalizeTaskCommentBody(comment.body)
  const form = useForm({ body: normalizedBody, lock_version: comment.lock_version })
  const path = fdeProjectPath(`${fdeRoutes.projectTaskComments}/${comment.id}`, projectId, {
    project_task_id: taskId,
  })
  const Icon = comment.author_type === 'agent' ? Bot : UserRound
  // Only long agent updates start folded; the full evidence stays one click away.
  const collapsible =
    comment.author_type === 'agent' && (normalizedBody.length > 480 || normalizedBody.split('\n').length > 8)

  function update() {
    form.transform((data) => ({ project_task_comment: data }))
    form.patch(path, { preserveScroll: true, preserveState: true, onSuccess: () => setEditing(false) })
  }
  function remove() {
    if (!window.confirm(copy.common.confirm_delete)) return
    router.delete(path, { preserveScroll: true, preserveState: true })
  }

  return (
    <article className="flex gap-3">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground">
        {comment.avatar_url ? (
          <img src={comment.avatar_url} alt="" className="size-full object-cover" />
        ) : (
          <Icon className="size-3.5" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <strong className="text-foreground">{comment.author_label}</strong>
          <time dateTime={comment.created_at} className="text-muted-foreground">
            {new Intl.DateTimeFormat('pt-BR', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            }).format(new Date(comment.created_at))}
          </time>
          {comment.edited_at && <span className="text-muted-foreground">· {copy.management.edited}</span>}
        </div>
        {editing ? (
          <div className="mt-2">
            <Textarea
              value={form.data.body}
              onChange={(event) => form.setData('body', event.target.value)}
              rows={3}
            />
            <div className="mt-2 flex justify-end gap-1">
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                aria-label={copy.common.cancel}
              >
                <X />
              </Button>
              <Button
                size="icon-sm"
                onClick={update}
                disabled={form.processing || !form.data.body.trim()}
                aria-label={copy.common.save}
              >
                <Check />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div
              id={bodyId}
              onFocus={() => {
                if (collapsible) setExpanded(true)
              }}
              className={cn('mt-2', collapsible && !expanded && 'line-clamp-4 max-h-24 overflow-hidden')}
            >
              <SimpleMarkdown
                content={normalizedBody}
                allowImages={false}
                className="break-words text-sm text-foreground/85 [&_code]:break-words [&_li]:leading-6 [&_ol]:mb-2 [&_ol]:list-outside [&_ol]:pl-5 [&_p]:mb-2 [&_p]:leading-6 [&_ul]:mb-2 [&_ul]:list-outside [&_ul]:pl-5"
              />
            </div>
            {collapsible && (
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={bodyId}
                onClick={() => setExpanded((value) => !value)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              >
                {expanded ? copy.management.collapse_update : copy.management.expand_update}
                {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
              </button>
            )}
          </>
        )}
      </div>
      {comment.can_edit && !editing && (
        <div className="flex shrink-0 gap-0.5">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            aria-label={copy.common.edit}
          >
            <Pencil />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            className="text-destructive"
            onClick={remove}
            aria-label={copy.common.delete}
          >
            <Trash2 />
          </Button>
        </div>
      )}
    </article>
  )
}
